import {
  ComputerDataItem,
  EventToolType,
  IStep,
  StepProcessStatus,
  IChatFile,
  IFeedbackData,
} from '@src/types';
import intl from '@/internationalzation/intl';
import { create } from 'zustand';
import { SubmitFeedback } from '@/services/walle/model/SubmitFeedback';

const handleFileType = (
  data: ComputerDataItem,
  activeFile: (data: IChatFile) => void,
) => {
  if (data.type === EventToolType.FILE_GENERATE) {
    const { file_extension, file_id, file_name, file_size, pdf_key } =
      data.data?.file ?? {};
    activeFile({
      fileExtension: file_extension ?? '',
      fileName: file_name ?? '',
      fileSize: file_size ?? 0,
      fileId: file_id ?? '',
      pdfKey: pdf_key ?? '',
    });
    return true;
  }
  return false;
};
interface ComputerDataModel {
  computerData: ComputerDataItem | null;
  fileData: IChatFile | null;
  computerDataList: ComputerDataItem[];
  activeComputerDataById: (id: string, extraState?: any) => void;
  activeIndex: number;
  isMinimized: boolean;
  setIsMinimized: (isMinimized: boolean) => void;
  toRightSide: () => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  vncViewOnly: boolean;
  appendComputerData: (data: ComputerDataItem) => void;
  upsertComputerData: (data: ComputerDataItem, replaceId?: string) => void;
  updateComputerDataById: (
    id: string,
    updater: (data: ComputerDataItem) => ComputerDataItem,
  ) => void;
  activeVNC: (viewOnly?: boolean) => void;
  activeComputerDataByIndex: (index: number) => void;
  mode: 'computer' | 'file';
  activeFile: (data: IChatFile) => void;
  stepsData: IStep[];
  currentStep: IStep;
  stepIndex: number;
  setStepsData: (data: IStep[]) => void;
  activeStepByContent: (content: string) => void;
  deactiveFile: () => void;
  onStepManualStop: () => void;
  resetComputerData: () => void;
  feedbackData: Map<string, IFeedbackData>;
  setFeedbackData: (data: Record<string, IFeedbackData>) => void;
}
export const useComputerData = create<ComputerDataModel>((set, get) => ({
  computerData: null,
  fileData: null,
  computerDataList: [],
  activeIndex: 0,
  isMinimized: localStorage.getItem('mario-computer-minimized') === 'true',
  visible: false,
  mode: 'computer',
  stepsData: [],
  currentStep: {} as IStep,
  stepIndex: -1,
  vncViewOnly: true,
  feedbackData: new Map<string, IFeedbackData>(),
  resetComputerData: () => {
    set({
      computerData: null,
      fileData: null,
      computerDataList: [] as ComputerDataItem[],
      activeIndex: 0,
      stepsData: [] as IStep[],
      currentStep: {} as IStep,
      stepIndex: -1,
      isMinimized: localStorage.getItem('mario-computer-minimized') === 'true',
      visible: false,
      mode: 'computer',
      vncViewOnly: true,
    });
  },
  activeComputerDataById: (id: string, extraState: any = {}) => {
    const index = get().computerDataList.findIndex((item) => item.id === id);
    if (index !== -1) {
      const currentData = get().computerData;
      const nextData = get().computerDataList[index];
      if (
        currentData?.type === EventToolType.VNC &&
        nextData.type === EventToolType.BROWSER
      ) {
        return;
      }
      if (handleFileType(nextData, get().activeFile)) {
        return;
      }
      set({
        computerData: nextData,
        activeIndex: index,
        visible: true,
        mode: 'computer',
        ...extraState,
      });
    }
  },
  activeComputerDataByIndex: (index: number) => {
    const nextData = get().computerDataList[index];
    if (handleFileType(nextData, get().activeFile)) {
      return;
    }
    set({
      computerData: nextData,
      activeIndex: index,
      visible: true,
      mode: 'computer',
    });
  },
  appendComputerData: (data: ComputerDataItem) => {
    const computerDataList = get().computerDataList;
    const index = computerDataList.findIndex((item) => item.id === data.id);
    if (index === -1) {
      computerDataList.push(data);
      set({
        computerDataList: [...computerDataList],
      });
    }
  },
  upsertComputerData: (data: ComputerDataItem, replaceId: string = '') => {
    const computerDataList = get().computerDataList;
    const normalizedReplaceId = replaceId.trim();
    const targetId = normalizedReplaceId || data.id;
    const index = computerDataList.findIndex((item) => item.id === targetId);
    if (index !== -1) {
      const updatedList = [...computerDataList];
      updatedList[index] = data;
      set({
        computerDataList: updatedList,
      });
      const currentData = get().computerData;
      if (currentData?.id === targetId) {
        set({
          computerData: data,
        });
      }
      return;
    }
    const duplicatedIndex = computerDataList.findIndex(
      (item) => item.id === data.id,
    );
    if (duplicatedIndex !== -1) {
      const updatedList = [...computerDataList];
      updatedList[duplicatedIndex] = data;
      set({
        computerDataList: updatedList,
      });
      const currentData = get().computerData;
      if (currentData?.id === data.id) {
        set({
          computerData: data,
        });
      }
      return;
    }
    set({
      computerDataList: [...computerDataList, data],
    });
  },
  updateComputerDataById: (
    id: string,
    updater: (data: ComputerDataItem) => ComputerDataItem,
  ) => {
    const computerDataList = get().computerDataList;
    const index = computerDataList.findIndex((item) => item.id === id);
    if (index !== -1) {
      const updatedList = [...computerDataList];
      updatedList[index] = updater(updatedList[index]);
      set({
        computerDataList: updatedList,
      });
      // 如果当前激活的数据是更新的数据，也需要更新
      const currentData = get().computerData;
      if (currentData?.id === id) {
        set({
          computerData: updatedList[index],
        });
      }
    }
  },
  setIsMinimized: (isMinimized: boolean) => {
    localStorage.setItem('mario-computer-minimized', String(isMinimized));
    set({
      isMinimized,
    });
  },
  toRightSide: () => {
    localStorage.setItem('mario-computer-minimized', String(false));
    set({
      isMinimized: false,
      visible: true,
      mode: 'computer',
    });
  },
  setVisible: (visible: boolean) => {
    set({
      visible,
    });
  },
  activeVNC: (viewOnly: boolean = true) => {
    if (
      get().computerData?.type === EventToolType.VNC &&
      get().vncViewOnly === viewOnly
    ) {
      return;
    }
    set({
      vncViewOnly: viewOnly,
      computerData: {
        type: EventToolType.VNC,
        icon: 'https://s2-11289.kwimgs.com/kos/nlav11289/16/eed_wali_icon_03_action_wangluo_line.png',
        id: 'vnc',
        title: 'VNC',
        toolName: intl.t('远程浏览器'),
      },
      visible: true,
      mode: 'computer',
      isMinimized: false,
    });
  },

  activeFile: (data: IChatFile) => {
    set({
      fileData: data,
      visible: true,
      mode: 'file',
      isMinimized: true,
    });
  },
  deactiveFile: () => {
    set({
      fileData: null,
      visible: true,
      mode: 'computer',
      isMinimized: true,
    });
  },
  setStepsData: (data: IStep[]) => {
    let stepIndex = data.findIndex((item) =>
      [
        StepProcessStatus.RUNNING,
        StepProcessStatus.STOP,
        StepProcessStatus.MANUAL_STOP,
      ].includes(item.status),
    );
    if (stepIndex === -1) {
      stepIndex = data.length - 1;
    }
    set({
      stepsData: data,
      stepIndex,
      currentStep: data[stepIndex],
    });
  },
  activeStepByContent: (content: string) => {
    let stepIndex = get().stepsData.findIndex(
      (item) => item.content === content,
    );
    if (stepIndex === -1) {
      return;
    }
    set({
      stepIndex,
      currentStep: get().stepsData[stepIndex],
    });
  },
  onStepManualStop: () => {
    const stepsData = get().stepsData;
    let stepIndex = stepsData.findIndex(
      (item) => item.status === StepProcessStatus.RUNNING,
    );
    if (stepIndex !== -1) {
      stepsData[stepIndex].status = StepProcessStatus.MANUAL_STOP;
      set({
        stepsData,
        stepIndex,
        currentStep: stepsData[stepIndex],
      });
    }
  },
  setFeedbackData: (data: Record<string, IFeedbackData>) => {
    if (data) {
      set({
        feedbackData: new Map(Object.entries(data)),
      });
    } else {
      set({
        feedbackData: new Map(),
      });
    }
  },
}));
